import React, { useState, useEffect } from 'react';
import { contestAPI, walletAPI, memberAPI, playerAPI } from '../../services/api';
import './ContestDisplay.css';

function ContestDisplay({ 
  tournamentId, 
  playerGhin = null, // For public view authentication
  showPlayerActions = true, // Whether to show join/leave actions
  onWalletLoaded = null // Callback to pass wallet data to parent
}) {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerWallet, setPlayerWallet] = useState(null);
  const [playerMember, setPlayerMember] = useState(null);
  const [tournamentPlayer, setTournamentPlayer] = useState(null);
  const [loadingPlayerWallet, setLoadingPlayerWallet] = useState(false);
  const [joinLoading, setJoinLoading] = useState(new Set());

  useEffect(() => {
    loadContests();
    if (playerGhin && showPlayerActions) {
      loadPlayerData();
    }
  }, [tournamentId, playerGhin]);

  const loadContests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await contestAPI.getTournamentContests(tournamentId);
      setContests(response.data.data.contests || []);
    } catch (err) {
      console.error('Error loading contests:', err);
      setError('Failed to load contests');
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerData = async () => {
    if (!playerGhin) return;
    
    try {
      setLoadingPlayerWallet(true);
      
      // First, try to find the tournament player by GHIN
      const playersResponse = await playerAPI.getAll(tournamentId);
      
      // Handle different possible response structures - the API returns a direct array
      const players = Array.isArray(playersResponse.data) ? playersResponse.data : [];
      const tournamentPlayer = players.find(p => p.ghin === playerGhin);
      setTournamentPlayer(tournamentPlayer);
      
      // Also load member data for wallet
      const memberResponse = await memberAPI.getAll({ search: playerGhin });
      const member = memberResponse.data.data.members?.find(m => m.ghin === playerGhin);
      
      if (member) {
        const walletResponse = await memberAPI.getMemberWallet(member._id);
        const wallet = walletResponse.data.data.wallet;
        setPlayerWallet(wallet);
        setPlayerMember(member);
        
        // Pass wallet data to parent component
        if (onWalletLoaded && wallet) {
          onWalletLoaded(wallet);
        }
      }
    } catch (err) {
      console.error('Error loading player data:', err);
      // Don't show error - contest display is optional
    } finally {
      setLoadingPlayerWallet(false);
    }
  };

  const handleJoinContest = async (contestId) => {
    if (!playerGhin) return;

    // Find the contest to get entry fee
    const contest = contests.find(c => c._id === contestId);
    if (!contest) return;

    // Check if tournament player exists
    if (!tournamentPlayer) {
      alert('You are not registered for this tournament. Please contact the tournament director.');
      return;
    }

    // Check if wallet exists and has sufficient funds
    if (!playerWallet) {
      alert('No wallet found. Please contact the tournament director to set up your wallet.');
      return;
    }

    if (playerWallet.balance < contest.entryFee) {
      const shortfall = contest.entryFee - playerWallet.balance;
      alert(`Insufficient funds. You need ${formatCurrency(shortfall)} more in your wallet to join this contest.\n\nCurrent balance: ${formatCurrency(playerWallet.balance)}\nEntry fee: ${formatCurrency(contest.entryFee)}\n\nPlease contact the tournament director to add funds to your wallet.`);
      return;
    }

    setJoinLoading(prev => new Set(prev).add(contestId));
    
    try {
      // Add participant to contest using tournament player ID
      const playerId = tournamentPlayer._id;
      const playerName = tournamentPlayer.name || `${tournamentPlayer.firstName} ${tournamentPlayer.lastName}`;
      
      await contestAPI.addParticipant(contestId, playerId, playerName);
      
      // Process entry fee payment using tournament player ID
      await contestAPI.processEntryFee(contestId, playerId);
      
      // Reload contests and player data to reflect changes
      await Promise.all([loadContests(), loadPlayerData()]);
      
    } catch (err) {
      console.error('Error joining contest:', err);
      alert(err.response?.data?.message || 'Failed to join contest. Please try again.');
    } finally {
      setJoinLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(contestId);
        return newSet;
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusDisplay = (status) => {
    const statusConfig = {
      setup: { label: 'Setup', icon: '⚙️', className: 'status-setup' },
      collecting: { label: 'Open for Entry', icon: '📝', className: 'status-collecting' },
      active: { label: 'In Progress', icon: '🏌️', className: 'status-active' },
      'paying-out': { label: 'Paying Out', icon: '💰', className: 'status-paying' },
      closed: { label: 'Completed', icon: '✅', className: 'status-closed' },
      cancelled: { label: 'Cancelled', icon: '❌', className: 'status-cancelled' }
    };
    
    return statusConfig[status] || { label: status, icon: '❓', className: 'status-unknown' };
  };

  const isPlayerInContest = (contest) => {
    if (!playerGhin || !tournamentPlayer) return false;
    const playerId = tournamentPlayer._id;
    return contest.participants.some(p => 
      p.status === 'paid' && (p.playerName?.includes(playerGhin) || p.playerId === playerId)
    );
  };

  const canJoinContest = (contest) => {
    if (!playerGhin || !showPlayerActions) return false;
    if (!['setup', 'collecting', 'active'].includes(contest.status)) return false;
    if (isPlayerInContest(contest)) return false;
    return true; // Always allow join attempt, handle insufficient funds in the click handler
  };

  if (loading) {
    return (
      <div className="contest-display loading">
        <div className="loading-text">Loading contests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contest-display error">
        <div className="error-text">{error}</div>
      </div>
    );
  }

  if (!contests || contests.length === 0) {
    return null; // Don't show anything if no contests
  }

  // Only show contests that are joinable or that the player is already in
  const playerContests = contests.filter(contest => isPlayerInContest(contest));
  const availableContests = contests.filter(contest => 
    !isPlayerInContest(contest) && ['setup', 'collecting', 'active'].includes(contest.status)
  );

  return (
    <div className="contest-display">
      {/* Player's Contests */}
      {playerContests.length > 0 && (
        <div className="contest-section">
          <div className="contest-grid">
            {playerContests.map(contest => {
              const statusInfo = getStatusDisplay(contest.status);
              return (
                <div key={contest._id} className="contest-card player-contest">
                  <div className="contest-header">
                    <div className="contest-name">{contest.name}</div>
                    <div className={`contest-status ${statusInfo.className}`}>
                      <span className="status-icon">{statusInfo.icon}</span>
                      <span className="status-text">{statusInfo.label}</span>
                    </div>
                  </div>
                  <div className="contest-details">
                    <div className="contest-info">
                      <div className="contest-type">{contest.contestTypeId?.name || 'Unknown Type'}</div>
                      <div className="contest-description">{contest.contestTypeId?.description || ''}</div>
                    </div>
                    <div className="contest-finances">
                      <div className="finance-item">
                        <span className="finance-label">Entry Fee:</span>
                        <span className="finance-value">{formatCurrency(contest.entryFee)}</span>
                      </div>
                      <div className="finance-item">
                        <span className="finance-label">Total Pot:</span>
                        <span className="finance-value">{formatCurrency(contest.accounting?.totalPot || 0)}</span>
                      </div>
                      <div className="finance-item">
                        <span className="finance-label">Participants:</span>
                        <span className="finance-value">{contest.accounting?.participantCount || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Contests */}
      {availableContests.length > 0 && (
        <div className="contest-section">
          <div className="contest-grid">
            {availableContests.map(contest => {
              const statusInfo = getStatusDisplay(contest.status);
              const canJoin = canJoinContest(contest);
              const isJoining = joinLoading.has(contest._id);
              
              return (
                <div key={contest._id} className="contest-card available-contest">
                  <div className="contest-header">
                    <div className="contest-name">{contest.name}</div>
                    <div className={`contest-status ${statusInfo.className}`}>
                      <span className="status-icon">{statusInfo.icon}</span>
                      <span className="status-text">{statusInfo.label}</span>
                    </div>
                  </div>
                  <div className="contest-details">
                    <div className="contest-info">
                      <div className="contest-type">{contest.contestTypeId?.name || 'Unknown Type'}</div>
                      <div className="contest-description">{contest.contestTypeId?.description || ''}</div>
                    </div>
                    <div className="contest-finances">
                      <div className="finance-item">
                        <span className="finance-label">Entry Fee:</span>
                        <span className="finance-value">{formatCurrency(contest.entryFee)}</span>
                      </div>
                      <div className="finance-item">
                        <span className="finance-label">Current Pot:</span>
                        <span className="finance-value">{formatCurrency(contest.accounting?.totalPot || 0)}</span>
                      </div>
                      <div className="finance-item">
                        <span className="finance-label">Participants:</span>
                        <span className="finance-value">{contest.accounting?.participantCount || 0}</span>
                      </div>
                    </div>
                    {showPlayerActions && playerGhin && (
                      <div className="contest-actions">
                        {canJoin ? (
                          <button 
                            className="join-contest-btn"
                            onClick={() => handleJoinContest(contest._id)}
                            disabled={isJoining}
                          >
                            {isJoining ? 'Joining...' : `Join Contest - ${formatCurrency(contest.entryFee)}`}
                          </button>
                        ) : (
                          <div className="cannot-join">
                            Contest not available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}

export default ContestDisplay;